import * as React from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import type { SxProps, Theme } from '@mui/material';
import { inputBaseClasses, styled } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import OutlinedInput from '@mui/material/OutlinedInput';
import type { SelectChangeEvent, SelectProps } from '@mui/material/Select';
import Select from '@mui/material/Select';
import useId from '@mui/utils/useId';
type ValueType<T, M extends boolean | undefined> = M extends true ? T[] : T;
type OnChangeType<T, M extends boolean | undefined> = (vals: ValueType<T, M>) => void;
export interface VehicleSelectFilterProps<T extends string | number, M extends boolean | undefined = false> {
  id?: string;
  label: string;
  options: T[];
  multiple?: M;
  value: ValueType<T, M>;
  onChange: OnChangeType<T, M>;
  showAll?: boolean;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  formatOption?: (val: T) => React.ReactNode;
  MenuProps?: Partial<SelectProps<ValueType<T, M>>['MenuProps']>;
  emptyLabel?: string;
  showClearButton?: boolean;
}
const ClearIndicator = styled(IconButton, {
  name: 'TravviaVehicleSelectFilter',
  slot: 'ClearIndicator',
  overridesResolver: (_props, styles) => styles.clearIndicator
})({
  padding: 2,
  marginRight: -8,
  position: 'absolute',
  right: '35px',
  visibility: 'hidden'
});
export default function VehicleSelectFilter<T extends string | number, M extends boolean | undefined = false>({
  id,
  label,
  options,
  multiple,
  value,
  onChange,
  showAll,
  disabled,
  sx,
  formatOption,
  MenuProps,
  emptyLabel,
  showClearButton = true,
  ...props
}: VehicleSelectFilterProps<T, M>) {
  const elId = useId(id || 'vehicle-select-filter');
  const labelId = `${elId}-label`;
  const hasValue = value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : String(value).length > 0);
  const handleChange = (event: SelectChangeEvent<ValueType<T, M>>) => {
    const {
      target: {
        value: value_0
      }
    } = event;
    if (multiple) {
      const mappedValue = (typeof value_0 === 'string' ? value_0.split(',') : value_0) as ValueType<T, M>;
      onChange(mappedValue);
    } else {
      onChange(value_0 as ValueType<T, M>);
    }
  };
  return <FormControl fullWidth={true} disabled={disabled} sx={sx}>
            <InputLabel shrink={true} id={labelId}>
                {label}
            </InputLabel>
            <Select {...props} labelId={labelId} id={elId} multiple={multiple} value={value || ''} onChange={handleChange} sx={{
      [`& .${inputBaseClasses.focused},:hover`]: {
        '& .clearIndicator': {
          visibility: 'visible'
        }
      }
    }} input={<OutlinedInput label={label} endAdornment={showClearButton && hasValue ? <ClearIndicator className="clearIndicator" onClick={() => {
      onChange((multiple ? [] : '') as unknown as ValueType<T, M>);
    }} size="small" edge="end">
                                    <ClearIcon />
                                </ClearIndicator> : undefined} />} displayEmpty={true} renderValue={selected => {
      if (multiple) {
        const selectedArray = selected as T[];
        if (formatOption) {
          return <span>
                                    {selectedArray.map((val, idx) => <React.Fragment key={val}>
                                            {formatOption(val)}
                                            {idx < selectedArray.length - 1 ? ', ' : ''}
                                        </React.Fragment>)}
                                </span>;
        }
        if (selectedArray.length === 0) {
          return emptyLabel;
        }
        return selectedArray.join(', ');
      } else {
        const val_0 = selected as T;
        return (formatOption ? formatOption(val_0) : String(val_0)) || emptyLabel;
      }
    }} MenuProps={MenuProps}>
                {showAll ? <MenuItem value="">All</MenuItem> : null}
                {options.map(option => <MenuItem key={option} value={option}>
                        {multiple && <Checkbox checked={Array.isArray(value) && value.includes(option)} />}
                        <ListItemText primary={formatOption ? formatOption(option) : option} />
                    </MenuItem>)}
            </Select>
        </FormControl>;
}